/**
 * Approvals, on the REQUESTER's side — Step 47.
 *
 * Kai: *"people approving might not be agents or admins etc"* — and mostly
 * they are not. The person who signs off a laptop request is a line manager
 * with no agent licence and no reason to ever open the agent workspace.
 *
 * So the same engine gets a second door here, on the requester's own auth
 * domain, with `portalFetch`/`portalSend`. Not the agent page behind a looser
 * guard: the three domains never cross, and the decision itself still goes
 * through the one `decide()` on the server, so the rules cannot differ
 * between the two doors.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { portalFetch, portalSend } from '../../lib/portalApi';
import { fullTime } from '../../lib/time';
import { ticketRef } from '@itsm/shared';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';

interface Pending {
  requestId: string;
  stage: number;
  mode: string;
  note: string | null;
  requestedAt: number;
  onBehalfOf: string | null;
  entityId: string;
  ticketNumber: number | null;
  ticketSubject: string | null;
  ticketType: string | null;
}

export function PortalApprovalsPage() {
  const qc = useQueryClient();
  const [comment, setComment] = useState<Record<string, string>>({});
  // A typed comment is work — losing it on navigation with no prompt is what
  // the dirty guard exists to stop.
  const clearDirty = useDirty(Object.values(comment).some((c) => c.trim()));

  const mine = useQuery({
    queryKey: qk.approvals.portalMine,
    queryFn: () => portalFetch<{ entries: Pending[] }>('/api/portal/approvals'),
  });

  const answer = useMutation({
    mutationFn: (v: { requestId: string; status: 'approved' | 'rejected' }) =>
      portalSend(`/api/portal/approvals/${v.requestId}/decide`, 'POST', {
        status: v.status,
        comment: comment[v.requestId]?.trim() || null,
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not record that.');
      clearDirty();
      toast.success('Thank you — recorded.');
      void qc.invalidateQueries({ queryKey: qk.approvals.portalMine });
    },
  });

  const entries = mine.data?.entries ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Approvals</h1>
        <p className="text-sm text-muted-foreground">Requests waiting for your decision.</p>
      </div>

      {mine.isError && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-destructive">Could not load your approvals</p>
            <Button size="sm" variant="outline" onClick={() => void mine.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {mine.isPending && <Skeleton className="h-24 w-full" />}
      {mine.data && entries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-10 text-center">
            <ShieldCheck className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nothing is waiting for you.</p>
          </CardContent>
        </Card>
      )}

      {entries.map((e) => (
        <Card key={e.requestId}>
          <CardContent className="space-y-3 p-4">
            <div>
              <p className="text-sm font-medium">
                {e.ticketNumber ? `${ticketRef(e.ticketType ?? 'service_request', e.ticketNumber)} · ` : ''}
                {e.ticketSubject ?? 'A request'}
              </p>
              {e.note && <p className="mt-0.5 text-sm text-muted-foreground">{e.note}</p>}
              <p className="mt-0.5 text-xs text-muted-foreground">
                Asked {fullTime(e.requestedAt)}
                {/* Said plainly: approving as somebody's cover is a different
                    act from approving as yourself, and the record shows it. */}
                {e.onBehalfOf ? ' · you are covering a colleague' : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={comment[e.requestId] ?? ''}
                onChange={(ev) => setComment((c) => ({ ...c, [e.requestId]: ev.target.value }))}
                placeholder="Add a comment (optional)"
                aria-label="Comment for the approver"
                className="min-w-48 flex-1"
              />
              <Button
                onClick={() => answer.mutate({ requestId: e.requestId, status: 'approved' })}
                disabled={answer.isPending}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => answer.mutate({ requestId: e.requestId, status: 'rejected' })}
                disabled={answer.isPending}
              >
                Reject
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
